import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { ICategoriesResponse } from "@miplatform/types";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { useCategories, usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { SectionInfo } from "../../components/SectionInfo/SectionInfo";

export function Categories() {
  const [selectedCategory, setSelectedCategory] =
    useState<ICategoriesResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [search, setSearch] = useState("");

  const { navigate } = useRouter();
  const theme = useTheme();
  const debouncedSearch = useDebounce(search);
  const { hasPermission } = usePermissions();

  const canCreateCategory = hasPermission(PERMISSIONS.MATERIAL_CATEGORY.CREATE);
  const canDeleteCategory = hasPermission(PERMISSIONS.MATERIAL_CATEGORY.DELETE);
  const canUpdateCategory = hasPermission(PERMISSIONS.MATERIAL_CATEGORY.UPDATE);

  const {
    isLoading,
    getCategories,
    handleSetCategories,
    deleteCategory,
    refresh,
    categories,
    recentCategories,
    getRecentCategories,
  } = useCategories();

  const sections = [
    {
      fieldset: [
        {
          renderValue: (data: ICategoriesResponse) => (
            <SectionInfo data={data} />
          ),
        },
        {
          label: "Attributes",
          renderValue: (data: ICategoriesResponse) =>
            data.attributes?.map((a) => a.name).join(", ") || "--",
        },
        {
          label: "Steps",
          field: "steps",
        },
        {
          label: "Display Order",
          field: "displayOrder",
        },
      ],
    },
  ];

  const handleNavigate = (id: string) => {
    navigate(`/estimating/configuration/categories/${id}`);
  };

  const renderItemActions = (item: ICategoriesResponse) => {
    const actions = [];

    if (canUpdateCategory) {
      actions.push({
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item.id),
      });
    }

    if (canDeleteCategory) {
      actions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      });
    }

    return actions.length > 0 ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    ) : null;
  };

  const handleOpenDeleteModal = (item: ICategoriesResponse) => {
    setSelectedCategory(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedCategory) {
      deleteCategory(selectedCategory);
      setOpenDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Categories"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          canCreateCategory && (
            <MiButton
              title="Category"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() =>
                navigate("/estimating/configuration/add-categories")
              }
              data-testid="add-category-btn"
            />
          )
        }
        search={search}
        onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        actionContainerStyle={{
          flexWrap: "nowrap",
        }}
      />
      <MiBox>
        <MiList
          viewType="card"
          cardProps={{
            sections,
            renderItemActions,
            onClick: (item: ICategoriesResponse) => handleNavigate(item.id),
            leftCardProps: { fieldset: [] },
            showNulls: true,
          }}
          loading={isLoading}
          search={debouncedSearch}
          data={categories}
          setData={handleSetCategories}
          dataProvider={getCategories}
          recentData={recentCategories}
          recentDataProvider={getRecentCategories}
          refresh={refresh}
          paginationMode={PAGINATION.SERVER}
          emptyListProps={{
            text: "No Categories found",
          }}
          data-testid="categories-list"
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={openDeleteModal}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => setOpenDeleteModal(false)}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedCategory?.name}. Are you sure you would like to continue?`}
        />
      </MiBox>
    </>
  );
}
